import {
  IconButton,
  ITextProps,
  Label,
  Modal,
  Text,
  TextField,
} from "@fluentui/react";
import { AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import { ApiService } from "../../../services/api.service";
import { HelperService } from "../../../services/helper.service";
import {
  cancelIcon,
  contentStyles,
  iconButtonStyles,
  labelStyles,
  valueStyles,
} from "../../Controls/component-styles";
import {
  contextSegments,
  FormMode,
  InputType,
} from "../../Controls/context-segments";
import DeleteDialog from "../../Controls/Dialogs/DeleteDialog";
import { DynamicFormCommandBar } from "../../Controls/DynamicFormCommandBar/DynamicFormCommandBar";
import {
  attributes,
  ILoadingLocationDocument,
  LoadingLocation,
} from "./loading-location.type";

interface IProps {
  isModalOpen: boolean;
  selectedItem: ILoadingLocationDocument | null;
  formMode: FormMode;
  fkName?: string;
  closeModal: () => void;
  setFormMode: (formMode: FormMode) => void;
  setReload?: (reload: boolean) => void;
  setCreatedItem?: (e: any, newItem: any) => void;
}

const apiCalls: ApiService = ApiService.getInstance();
const helperService = HelperService.getInstance();
const formName: string = "loading-location-form";

const LoadingLocationForm = (props: IProps) => {
  const formHeading: string = helperService.getFormHeading(
    contextSegments.loadingLocation.singularName,
    props.formMode
  );

  const [payload, setPayload] = useState<ILoadingLocationDocument>(
    new LoadingLocation()
  );
  const [hideDeleteDialog, toggleHideDeleteDialog] = useState<boolean>(true);

  useEffect(() => {
    // In case some item was passed to the form
    // Set this item to the payload
    if (props.selectedItem) {
      setPayload(props.selectedItem);
    }
    // In case no item was passed to the form
    // Set default empty payload
    else {
      setPayload(new LoadingLocation());
    }

    // Clear the input when closing the modal
    if (!props.isModalOpen) {
      setPayload(new LoadingLocation());
    }
  }, [props.selectedItem, props.isModalOpen]);

  const titleId = helperService.makeId(10);

  /**
   * Invoke create action
   * @param e
   */
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // In case payload id exist
    // Update the item
    if (payload.id) {
      apiCalls
        .updateLoadingLocation(payload, payload.id)
        .then((res: AxiosResponse<any>) => {
          props.setReload && props.setReload(true);

          props.closeModal();
          setPayload(new LoadingLocation());
        });
    }
    // In case no payload id exist
    // Create a new item
    else {
      apiCalls
        .createLoadingLocation(payload)
        .then((res: AxiosResponse<any>) => {
          //   In case creating an item through the dropdown context
          if (props.setCreatedItem && props.fkName && res.data.id) {
            const fakeEvent = {
              target: {
                name: props.fkName,
                type: "dropdown",
              },
            };
            const newItem: ILoadingLocationDocument = {
              id: res.data["id"],
              city: res.data["city"],
              country: res.data["country"],
              street: res.data["street"],
              zip: res.data["zip"],
              contactPerson: res.data["contactPerson"],
              mail: res.data["mail"],
              phone: res.data["phone"],
              fax: res.data["fax"],
              exportCustomsOffice: res.data["exportCustomsOffice"],
              originCountry: res.data["originCountry"],
              code: res.data["code"],
            };
            props.setCreatedItem(fakeEvent, newItem);
          }
          props.setReload && props.setReload(true);

          props.closeModal();
          setPayload(new LoadingLocation());
        });
    }
  };

  /**
   * Write newValue to state by element name
   * @param e
   * @param newValue
   */
  const handleOnChange = (e: any, newValue: any) => {
    const { name } = e.target;

    if (newValue === undefined) {
      setPayload({ ...payload, [name]: null });
    } else {
      const inputType: string = e.target.type;
      if (inputType === InputType.text || inputType === InputType.textarea) {
        setPayload({ ...payload, [name]: String(newValue) });
      } else if (inputType === InputType.number) {
        setPayload({ ...payload, [name]: Number(newValue) });
      } else if (inputType === InputType.checkbox) {
        setPayload({ ...payload, [name]: Boolean(newValue) });
      } else if (inputType === InputType.dropdown) {
        setPayload({ ...payload, [name]: newValue });
      } else if (inputType === InputType.combobox) {
        setPayload({ ...payload, [name]: newValue.text });
      }
    }
  };
  const isEditable: boolean =
    props.formMode === FormMode.create || props.formMode === FormMode.edit;

  const deleteOpenedItem = async () => {
    toggleHideDeleteDialog(true);
    props.closeModal();
    await apiCalls.deleteLoadingLocation(props.selectedItem!.id!);
    props.setReload && props.setReload(true);
  };

  return (
    <Modal
      titleAriaId={titleId}
      isOpen={props.isModalOpen}
      onDismiss={props.closeModal}
      isBlocking={false}
      containerClassName={contentStyles.container}
    >
      <DeleteDialog
        toggleHideDeleteDialog={toggleHideDeleteDialog}
        hideDeleteDialog={hideDeleteDialog}
        selectedItemsCount={1}
        deleteSelectedItems={deleteOpenedItem}
      />
      <div className={contentStyles.header}>
        <Text id={titleId} variant={"xxLarge" as ITextProps["variant"]}>
          {formHeading}
        </Text>
        <IconButton
          styles={iconButtonStyles}
          iconProps={cancelIcon}
          ariaLabel="Close popup modal"
          onClick={props.closeModal}
        />
      </div>
      <div className={contentStyles.body}>
        {isEditable && props.isModalOpen ? (
          <form id={formName} onSubmit={onSubmit}>
            <DynamicFormCommandBar
              formMode={props.formMode}
              toggleHideDeleteDialog={toggleHideDeleteDialog}
              closeModal={props.closeModal}
              formName={formName}
            />
            <TextField
              name={attributes.city.name}
              label={attributes.city.label}
              type="text"
              required
              onChange={handleOnChange}
              defaultValue={payload.city}
            />
            <TextField
              name={attributes.country.name}
              label={attributes.country.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.country}
            />
            <TextField
              name={attributes.street.name}
              label={attributes.street.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.street}
            />
            <TextField
              name={attributes.zip.name}
              label={attributes.zip.label}
              type="text"
              maxLength={5}
              onChange={handleOnChange}
              defaultValue={payload.zip}
            />
            <TextField
              name={attributes.exportCustomsOffice.name}
              label={attributes.exportCustomsOffice.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.exportCustomsOffice}
            />
            <TextField
              name={attributes.contactPerson.name}
              label={attributes.contactPerson.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.contactPerson}
            />
            <TextField
              name={attributes.mail.name}
              label={attributes.mail.label}
              type="email"
              onChange={handleOnChange}
              defaultValue={payload.mail}
            />
            <TextField
              name={attributes.phone.name}
              label={attributes.phone.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.phone}
            />
            <TextField
              name={attributes.fax.name}
              label={attributes.fax.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.fax}
            />

            <TextField
              name={attributes.originCountry.name}
              label={attributes.originCountry.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.originCountry}
            />
            <TextField
              name={attributes.code.name}
              label={attributes.code.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.code}
            />
          </form>
        ) : (
          <div>
            <DynamicFormCommandBar
              formMode={props.formMode}
              toggleHideDeleteDialog={toggleHideDeleteDialog}
              formName={formName}
              setFormMode={props.setFormMode}
              closeModal={props.closeModal}
            />
            <Label styles={labelStyles}>{attributes.city.label}</Label>
            <span style={valueStyles}>{payload.city}</span>
            <Label styles={labelStyles}>{attributes.country.label}</Label>
            <span style={valueStyles}>{payload.country}</span>
            <Label styles={labelStyles}>{attributes.street.label}</Label>
            <span style={valueStyles}>{payload.street}</span>
            <Label styles={labelStyles}>{attributes.zip.label}</Label>
            <span style={valueStyles}>{payload.zip}</span>
            <Label styles={labelStyles}>{attributes.contactPerson.label}</Label>
            <span style={valueStyles}>{payload.contactPerson}</span>
            <Label styles={labelStyles}>{attributes.mail.label}</Label>
            <span style={valueStyles}>{payload.mail}</span>
            <Label styles={labelStyles}>{attributes.phone.label}</Label>
            <span style={valueStyles}>{payload.phone}</span>
            <Label styles={labelStyles}>{attributes.fax.label}</Label>
            <span style={valueStyles}>{payload.fax}</span>
            <Label styles={labelStyles}>
              {attributes.exportCustomsOffice.label}
            </Label>
            <span style={valueStyles}>{payload.exportCustomsOffice}</span>
            <Label styles={labelStyles}>{attributes.originCountry.label}</Label>
            <span style={valueStyles}>{payload.originCountry}</span>
            <Label styles={labelStyles}>{attributes.code.label}</Label>
            <span style={valueStyles}>{payload.code}</span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LoadingLocationForm;
